/**
 * The self-explanation sentence of one attempt, between the moment it is typed and the moment the
 * server has it (pedagogy spec §5.3.6): the draft and its "saved" mark per `attemptId`, kept in
 * `sessionStorage` so a refresh keeps both, and the one request in flight per attempt, so the
 * field that unmounts ("Nästa", "Kontrollera igen") and the field that mounts for the same
 * attempt ("Jag kör fast" keeps the first try's id) never post the same sentence twice.
 *
 * Pure of React. Storage can be unavailable (private window, blocked site data): every access is
 * guarded and the field then works for the life of the page only.
 */

const STORAGE_KEY = "mf.practice.selfExplanation";
/** Attempts remembered; the oldest go first. A session holds a few dozen at most. */
const MAX_ENTRIES = 40;

export interface SentenceDraft {
  /** What was typed (not trimmed). */
  text: string;
  /** The server has this attempt's sentence; the field is read-only from then on. */
  saved: boolean;
}

type Stored = Record<string, SentenceDraft>;

function readAll(): Stored {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const out: Stored = {};
    for (const [id, value] of Object.entries(parsed)) {
      const draft = value as Partial<SentenceDraft> | null;
      if (draft && typeof draft.text === "string") {
        out[id] = { text: draft.text, saved: draft.saved === true };
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** The remembered draft of an attempt; empty and unsaved when there is none. */
export function readDraft(attemptId: string): SentenceDraft {
  return readAll()[attemptId] ?? { text: "", saved: false };
}

/** Remembers the draft; an empty unsaved one is forgotten. Never throws. */
export function writeDraft(attemptId: string, draft: SentenceDraft): void {
  try {
    const all = readAll();
    delete all[attemptId];
    if (draft.text !== "" || draft.saved) all[attemptId] = draft;
    const kept = Object.entries(all).slice(-MAX_ENTRIES);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    // Storage is unavailable: the draft lives in the component only.
  }
}

/** Forgets every remembered draft (sign-out: the next account must not see the last one's). */
export function clearDrafts(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage is unavailable: nothing was kept.
  }
}

const inFlight = new Map<string, Promise<boolean>>();

/** The request currently saving this attempt's sentence, if any (resolves true once saved). */
export function sendingFor(attemptId: string): Promise<boolean> | undefined {
  return inFlight.get(attemptId);
}

/**
 * Saves the trimmed sentence once: a call while one is in flight for the same attempt joins it. On
 * success the draft is remembered as saved; resolves false on any failure (nothing is thrown, so
 * "Nästa" can never be blocked by it) and leaves the draft editable.
 */
export function sendSentence(
  attemptId: string,
  text: string,
  send: (body: { attemptId: string; text: string }) => Promise<unknown>,
): Promise<boolean> {
  const running = inFlight.get(attemptId);
  if (running) return running;
  const sentence = text.trim();
  const request = send({ attemptId, text: sentence })
    .then(
      () => {
        writeDraft(attemptId, { text: sentence, saved: true });
        return true;
      },
      () => false,
    )
    .finally(() => {
      inFlight.delete(attemptId);
    });
  inFlight.set(attemptId, request);
  return request;
}
